// src/admin/hooks/useDashboardStats.ts
// At-a-glance numbers for the admin landing screen. Every figure is a single
// count/aggregate against its home table; all fetched in parallel for latency.
//
// Schema alignment vs. earlier spec:
//   - payments.direction enum is 'disbursement' | 'repayment' (NOT inbound/outbound)
//   - payments uses completed_at for the completion timestamp (there is no received_at)
//   - loan_repayment_schedule.status enum is 'pending' | 'partial' | 'paid' | 'overdue'
//     (NOT 'scheduled' — that was early-draft terminology)
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';

export interface DashboardStats {
  pending_approval: number;
  kyc_pending: number;
  delinquent: number;
  payments_today: number;
  disbursed_today_ngwee: number;
  active_loans: number;
  outstanding_ngwee: number;
  action_requests_pending: number;
  ptp_due_today: number;
}

/** Start of today (local midnight) as ISO string for >= comparisons. */
function startOfTodayISO(): string {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return midnight.toISOString();
}

/** YYYY-MM-DD for today in local timezone (used to match date columns). */
function todayDateString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function useDashboardStats() {
  return useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    staleTime: 60_000,
    queryFn: async () => {
      const todayStart = startOfTodayISO();
      const todayDate = todayDateString();

      // Run each aggregate in parallel. head:true + count:'exact' is cheap.
      const [
        pending,
        kyc,
        delinq,
        active,
        actions,
        ptp,
        paysToday,
        disbToday,
        outstanding,
      ] = await Promise.all([
        supabase.from('loans').select('id', { count: 'exact', head: true })
          .eq('status', 'pending_approval'),
        supabase.from('kyc_submissions').select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase.from('loans').select('id', { count: 'exact', head: true })
          .in('status', ['delinquent']),
        supabase.from('loans').select('id', { count: 'exact', head: true })
          .eq('status', 'active'),
        supabase.from('admin_action_requests').select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase.from('collection_contacts').select('id', { count: 'exact', head: true })
          .eq('outcome', 'reached_promise')
          .eq('ptp_date', todayDate),
        supabase.from('payments').select('id', { count: 'exact', head: true })
          .eq('direction', 'repayment').eq('status', 'completed')
          .gte('completed_at', todayStart),
        supabase.from('payments').select('amount_ngwee')
          .eq('direction', 'disbursement').eq('status', 'completed')
          .gte('completed_at', todayStart),
        supabase.from('loan_repayment_schedule').select('scheduled_amount_ngwee, paid_amount_ngwee')
          .in('status', ['pending', 'partial', 'overdue']),
      ]);

      const sumAmounts = (rows: Array<{ amount_ngwee: number }> | null | undefined): number =>
        (rows ?? []).reduce((s, r) => s + Number(r.amount_ngwee ?? 0), 0);

      const sumOutstanding = (
        rows: Array<{ scheduled_amount_ngwee: number; paid_amount_ngwee: number }> | null | undefined,
      ): number =>
        (rows ?? []).reduce(
          (s, r) => s + Number(r.scheduled_amount_ngwee ?? 0) - Number(r.paid_amount_ngwee ?? 0),
          0,
        );

      return {
        pending_approval: pending.count ?? 0,
        kyc_pending: kyc.count ?? 0,
        delinquent: delinq.count ?? 0,
        payments_today: paysToday.count ?? 0,
        disbursed_today_ngwee: sumAmounts(disbToday.data),
        active_loans: active.count ?? 0,
        outstanding_ngwee: sumOutstanding(outstanding.data),
        action_requests_pending: actions.count ?? 0,
        ptp_due_today: ptp.count ?? 0,
      };
    },
  });
}
